<template>
  <div class="home">
    <!-- 头部导航 -->
    <Header />

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- Hero区域 - 背景古画轮播 -->
      <section class="hero-section" @mouseenter="showArrows" @mouseleave="hideArrows">
        <div class="hero-carousel">
          <el-carousel
            ref="carousel"
            :interval="5000"
            :arrow="'never'"
            :indicator-position="bannerImages.length > 1 ? '' : 'none'"
            height="100%"
          >
            <el-carousel-item v-for="(image, index) in bannerImages" :key="index">
              <div 
                class="hero-bg" 
                :style="{ backgroundImage: `url(${image})` }"
              ></div>
            </el-carousel-item>
          </el-carousel>
        </div>
        
        <!-- 自定义左右箭头 -->
        <div v-show="bannerImages.length > 0" class="custom-arrows" :class="{ 'show': arrowsVisible }">
          <div class="arrow-left" @click="prevSlide">
            <i class="el-icon-arrow-left"></i>
          </div>
          <div class="arrow-right" @click="nextSlide">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        
        <div class="hero-overlay"></div>
        <div class="container">
          <div class="hero-content">
            <!-- 搜索框 -->
            <SearchBox @search="handleSearch" @category-change="handleCategoryChange" />

            <!-- 加载状态 -->
            <div v-if="bannerLoading" class="banner-loading">
              <i class="el-icon-loading"></i>
              <span>加载中...</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 传世名画展示 -->
      <section class="artworks-section" v-if="categories.includes('传世名画')">
        <div class="container">
          <h2 class="section-title">{{ categories[0] || '传世名画' }}</h2>
          
          <div v-if="homePageLoading" class="loading-state">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
          
          <div v-else class="artworks-container">
            <!-- 第一部分：6张图（左右布局） -->
            <div class="artworks-section-one">
              <!-- 左半部分：前两张大图（垂直排列） -->
              <div class="left-section">
                <div
                  v-for="artwork in paintingWorks.slice(0, 2)"
                  :key="artwork.id"
                  class="artwork-card large-card classical-card"
                  @click="viewArtwork(artwork)"
                >
                  <div class="artwork-image">
                    <img :src="artwork.encoding" :alt="artwork.name" loading="lazy">
                  </div>
                  <div class="artwork-title-bottom">{{ artwork.name }}</div>
                </div>
              </div>
              
              <!-- 右半部分：后四张图（2x2网格） -->
              <div class="right-section">
                <div class="small-grid">
                  <div
                    v-for="artwork in paintingWorks.slice(2, 6)"
                    :key="artwork.id"
                    class="artwork-card small-card classical-card"
                    @click="viewArtwork(artwork)"
                  >
                    <div class="artwork-image">
                      <img :src="artwork.encoding" :alt="artwork.name" loading="lazy">
                    </div>
                    <div class="artwork-title-bottom">{{ artwork.name }}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 第二部分：4张图（4等份） -->
            <div class="artworks-section-two">
              <div class="equal-artworks">
                <div
                  v-for="artwork in paintingWorks.slice(6, 10)"
                  :key="artwork.id"
                  class="artwork-card equal-card classical-card"
                  @click="viewArtwork(artwork)"
                >
                  <div class="artwork-image">
                    <img :src="artwork.encoding" :alt="artwork.name" loading="lazy">
                  </div>
                  <div class="artwork-title-bottom">{{ artwork.name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 书法作品展示 -->
      <section class="calligraphy-section" v-if="categories.includes('传世名帖')">
        <div class="container">
          <h2 class="section-title">{{ categories[1] || '传世名帖' }}</h2>
          
          <div v-if="homePageLoading" class="loading-state">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
          
          <div v-else class="calligraphy-container">
            <!-- 第一行：6张图 -->
            <div class="calligraphy-row-one">
              <!-- 第一份：3张图（上2张，下1张） -->
              <div class="section-three-images">
                <!-- 上面2张 -->
                <div class="top-two-images">
                  <div
                    v-for="work in calligraphyWorks.slice(0, 2)"
                    :key="work.id"
                    class="calligraphy-card small-cal-card classical-card"
                    @click="viewCalligraphy(work)"
                  >
                    <div class="calligraphy-image">
                      <img :src="work.encoding" :alt="work.name" loading="lazy">
                    </div>
                    <div class="calligraphy-info">
                      <h3 class="calligraphy-title">{{ work.name }}</h3>
                    </div>
                  </div>
                </div>
                <!-- 下面1张 -->
                <div class="bottom-one-image">
                  <div
                    v-if="calligraphyWorks[2]"
                    :key="calligraphyWorks[2].id"
                    class="calligraphy-card medium-cal-card classical-card"
                    @click="viewCalligraphy(calligraphyWorks[2])"
                  >
                    <div class="calligraphy-image">
                      <img :src="calligraphyWorks[2].encoding" :alt="calligraphyWorks[2].name" loading="lazy">
                    </div>
                    <div class="calligraphy-info">
                      <h3 class="calligraphy-title">{{ calligraphyWorks[2].name }}</h3>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 第二份：2张图（上下排列） -->
              <div class="section-two-images">
                <div
                  v-for="work in calligraphyWorks.slice(3, 5)"
                  :key="work.id"
                  class="calligraphy-card medium-cal-card classical-card"
                  @click="viewCalligraphy(work)"
                >
                  <div class="calligraphy-image">
                    <img :src="work.encoding" :alt="work.name" loading="lazy">
                  </div>
                  <div class="calligraphy-info">
                    <h3 class="calligraphy-title">{{ work.name }}</h3>
                  </div>
                </div>
              </div>

              <!-- 第三份：1张图 -->
              <div class="section-one-image">
                <div
                  v-if="calligraphyWorks[5]"
                  :key="calligraphyWorks[5].id"
                  class="calligraphy-card large-cal-card classical-card"
                  @click="viewCalligraphy(calligraphyWorks[5])"
                >
                  <div class="calligraphy-image">
                    <img :src="calligraphyWorks[5].encoding" :alt="calligraphyWorks[5].name" loading="lazy">
                  </div>
                  <div class="calligraphy-info">
                    <h3 class="calligraphy-title">{{ calligraphyWorks[5].name }}</h3>
                  </div>
                </div>
              </div>
            </div>

            <!-- 第二行：4张图 -->
            <div class="calligraphy-row-two">
              <div
                v-for="work in calligraphyWorks.slice(6, 10)"
                :key="work.id"
                class="calligraphy-card equal-cal-card classical-card"
                @click="viewCalligraphy(work)"
              >
                <div class="calligraphy-image">
                  <img :src="work.encoding" :alt="work.name" loading="lazy">
                </div>
                <div class="calligraphy-info">
                  <h3 class="calligraphy-title">{{ work.name }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 底部 -->
    <Footer :config-data="configData" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { apiService } from '@/api'
import Header from '@/components/layout/Header.vue'
import Footer from '@/components/layout/Footer.vue'
import SearchBox from '@/components/search/SearchBox.vue'
import ipCheckManager from '@/utils/ipCheck'

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    SearchBox
  },
  data() {
    return {
      bannerImages: [],
      bannerLoading: true,
      
      // 首页数据
      homePageData: null,
      homePageLoading: true,
      categories: [],
      paintingWorks: [],
      calligraphyWorks: [],
      
      // 配置信息
      configData: {
        name: '温故科技',
        contact: 'wengukeji@163.com',
        beian: '京ICP备2022006303号'
      },
      
      // 箭头显示状态
      arrowsVisible: false, // 默认隐藏，悬浮时显示
    }
  },
  
  computed: {
    ...mapGetters(['isSmallScreen'])
  },
  
  async mounted() {
    this.initDeviceDetection()
    // 确保IP检测在首页加载时执行
    this.ensureIpCheck()
    await Promise.all([
      this.loadBannerImages(),
      this.loadHomePageData(),
      this.loadConfigData()
    ])
  },
  
  methods: {
    ...mapActions(['initDeviceDetection']),
    
    // 确保IP检测执行
    async ensureIpCheck() {
      try {
        const cachedIp = ipCheckManager.getCachedIpInfo()
        if (!cachedIp) {
          const ipInfo = await ipCheckManager.checkAndCacheIp()
          if (ipInfo) {
            this.$store.commit('SET_IP_INFO', ipInfo)
          }
        } else {
          this.$store.commit('SET_IP_INFO', cachedIp)
        }
      } catch (error) {
        // 静默处理错误
      }
    },
    
    // 加载banner图片
    async loadBannerImages() {
      try {
        this.bannerLoading = true
        const response = await apiService.getBanner()
        
        if (response && response.data && Array.isArray(response.data)) {
          this.bannerImages = response.data
          console.log('Banner images loaded:', this.bannerImages)
        } else {
          // 如果接口返回数据格式不正确，使用默认图片
          this.bannerImages = [
            'https://via.placeholder.com/1920x800/8B4513/ffffff?text=Classical+Chinese+Painting+1',
            'https://via.placeholder.com/1920x800/D2B48C/ffffff?text=Classical+Chinese+Painting+2',
            'https://via.placeholder.com/1920x800/B8860B/ffffff?text=Classical+Chinese+Painting+3'
          ]
        }
      } catch (error) {
        console.error('Failed to load banner images:', error)
        // 加载失败时使用默认图片
        this.bannerImages = [
          'https://via.placeholder.com/1920x800/8B4513/ffffff?text=Classical+Chinese+Painting+Default'
        ]
        this.$message.warning('轮播图加载失败，已使用默认图片')
      } finally {
        this.bannerLoading = false
      }
    },
    
    // 加载首页数据
    async loadHomePageData() {
      try {
        this.homePageLoading = true
        const response = await apiService.getHomePage()
        
        if (response && response.data) {
          this.homePageData = response.data
          this.categories = response.data.category_name || []
          
          // 根据接口返回的数据结构处理
          // 第一个分类使用category_1
          if (response.data.category_1 && Array.isArray(response.data.category_1)) {
            this.paintingWorks = response.data.category_1
          }
          
          // 第二个分类使用category_2  
          if (response.data.category_2 && Array.isArray(response.data.category_2)) {
            this.calligraphyWorks = response.data.category_2
          }
          
          console.log('首页数据加载成功:', this.homePageData)
          console.log('第一个分类作品 (category_1):', this.paintingWorks)
          console.log('第二个分类作品 (category_2):', this.calligraphyWorks)
        }
      } catch (error) {
        console.error('首页数据加载失败:', error)
        this.$message.warning('首页数据加载失败')
      } finally {
        this.homePageLoading = false
      }
    },
    
    // 加载配置数据
    async loadConfigData() {
      try {
        const response = await apiService.getConfigList()
        
        if (response && response.data) {
          // 更新配置数据，保持默认值作为后备
          this.configData = {
            name: response.data.name || this.configData.name,
            contact: response.data.contact || this.configData.contact,
            beian: response.data.beian || this.configData.beian
          }
          
          console.log('配置数据加载成功:', this.configData)
        }
      } catch (error) {
        console.error('配置数据加载失败:', error)
        this.$message.warning('配置数据加载失败，使用默认数据')
      }
    },
    
    
    handleSearch(searchData) {
      if (!searchData.keyword.trim()) {
        this.$message.warning('')
        return
      }
      // 这里可以处理搜索逻辑
    },
    
    handleCategoryChange(category) {
      console.log('分类变化:', category)
      // 这里可以处理分类变化逻辑
    },
    
    viewArtwork(artwork) {
      if (artwork && artwork.id) {
        this.$router.push(`/product/${artwork.id}`)
      } else {
        this.$message.warning('作品信息不完整')
      }
    },
    
    viewCalligraphy(work) {
      if (work && work.id) {
        this.$router.push(`/product/${work.id}`)
      } else {
        this.$message.warning('作品信息不完整')
      }
    },
    
    // 轮播图控制方法
    prevSlide() {
      console.log('点击左箭头', this.$refs.carousel)
      if (this.$refs.carousel) {
        this.$refs.carousel.prev()
      }
    },
    
    nextSlide() {
      console.log('点击右箭头', this.$refs.carousel)
      if (this.$refs.carousel) {
        this.$refs.carousel.next()
      }
    },
    
    // 箭头显示/隐藏控制
    showArrows() {
      console.log('显示箭头')
      this.arrowsVisible = true
    },
    
    hideArrows() {
      console.log('隐藏箭头')
      this.arrowsVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.home {
  min-height: 100vh;
  background: #FFFFFF;
  overflow-x: hidden;
  width: 100%;
}

// 主要内容
.main-content {
  padding-top: 0; // 移除顶部padding，让banner图片顶到页面顶部
  background-color: #FFFFFF !important;

  @media (max-width: 768px) {
    padding-top: 0;
  }
}

// Hero区域
.hero-section {
  position: relative;
  height: 60vh;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;

  @media (max-width: 768px) {
    height: 50vh;
    min-height: 300px;
  }
}

.hero-carousel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;

  :deep(.el-carousel) {
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  :deep(.el-carousel__container) {
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  :deep(.el-carousel__item) {
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  :deep(.el-carousel__indicators) {
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    margin: 0;
  }

  :deep(.el-carousel__indicators--outside) {
    bottom: 20px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    margin: 0;
  }

  :deep(.el-carousel__indicator) {
    .el-carousel__button {
      background-color: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.8);
    }

    &.is-active .el-carousel__button {
      background-color: white;
    }
  }

  // 隐藏原有的Element UI箭头
  :deep(.el-carousel__arrow) {
    display: none;
  }
}

// 自定义箭头样式
.custom-arrows {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10; // 降低z-index，确保不会覆盖搜索框
  opacity: 0;
  visibility: hidden; // 完全隐藏，避免占用空间
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none; // 默认不接收鼠标事件

  &.show {
    opacity: 1;
    visibility: visible; // 显示时恢复可见
    pointer-events: auto; // 显示时才接收鼠标事件
  }

  .arrow-left,
  .arrow-right {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    pointer-events: auto; // 箭头按钮本身需要接收点击事件

    &:hover {
      background: #fafafa;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      transform: translateY(-50%) scale(1.05);
    }

    i {
      font-size: 20px;
      color: #555;
      transition: color 0.3s ease;
    }

    &:hover i {
      color: var(--primary-color, #8B4513);
    }

    @media (max-width: 768px) {
      width: 40px;
      height: 40px;
      
      i {
        font-size: 16px;
      }
    }
  }

  .arrow-left {
    left: 30px;

    @media (max-width: 768px) {
      left: 15px;
    }
  }

  .arrow-right {
    right: 30px;

    @media (max-width: 768px) {
      right: 15px;
    }
  }
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.hero-content {
  position: relative;
  z-index: 100; // 提高z-index确保搜索框在最上层
  text-align: center;
  width: 100%;
  max-width: 800px;
  padding-top: 60px; // 给header留出空间，适配较小的banner高度

  @media (max-width: 768px) {
    padding-top: 50px;
  }
}

// 搜索框容器样式（由SearchBox组件处理）
:deep(.search-container) {
  margin-bottom: 40px;
}

.hero-dots {
  display: flex;
  justify-content: center;
  gap: 12px;

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: background 0.3s ease;

    &.active,
    &:hover {
      background: white;
    }
  }
}

.banner-loading,
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  padding: 40px 0;

  i {
    font-size: 20px;
    animation: rotate 2s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}

.loading-state {
  color: var(--text-secondary);
}

// 传世名画区域
.artworks-section {
  padding: 60px 0;
  background: #FFFFFF;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
}

.section-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 40px;
  border-bottom: 1px solid #ccc;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
}

.artworks-container {
  margin-top: 30px;
}

.artworks-section-one {
  display: grid;
  grid-template-columns: 1fr 1fr; // 左右各占50%
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.artworks-section-two {
  margin-bottom: 20px;
}

.left-section {
  display: grid;
  grid-template-columns: 1fr 1fr; // 左右两列（并排展示）
  gap: 16px;
}

.right-section {
  display: flex;
  align-items: stretch;
}

.small-grid {
  display: grid;
  grid-template-columns: 1fr 1fr; // 2列
  grid-template-rows: 1fr 1fr;    // 2行
  gap: 12px;
  width: 100%;
}

.equal-artworks {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.artwork-card {
  position: relative;
  cursor: pointer;

  .artwork-image {
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 8px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

  }

  &:hover {
    .artwork-image {
      img {
        transform: scale(1.05);
      }
    }
  }

  .artwork-title-bottom {
    text-align: left;
    padding: 12px 0 8px 0;
    font-size: 14px;
    // font-weight: 600;
  }
}

// 大图卡片样式（左侧并排）
.large-card {
  .artwork-image {
    height: 320px; // 扩大Y轴高度，不缩小X轴宽度

    @media (max-width: 768px) {
      height: 280px;
    }
  }

  .artwork-title-bottom {
    font-size: 14px;
    padding: 12px 10px 10px;
  }
}

// 中等卡片样式（与大图等高）
.medium-card {
  .artwork-image {
    height: 280px; // 与大图等高

    @media (max-width: 768px) {
      height: 240px;
    }
  }

  .artwork-title-bottom {
    font-size: 14px;
    padding: 14px 10px 10px;
  }
}

// 等份卡片样式
.equal-card {
  .artwork-image {
    height: 240px;

    @media (max-width: 768px) {
      height: 200px;
    }

    @media (max-width: 480px) {
      height: 220px;
    }
  }

  .artwork-title-bottom {
    font-size: 14px;
    padding: 12px 10px 10px;
  }
}

// 小图卡片样式（右侧2x2网格）
.small-card {
  .artwork-image {
    height: 140px; // 调整以匹配左侧大图总高度

    @media (max-width: 768px) {
      height: 120px;
    }

    @media (max-width: 480px) {
      height: 140px;
    }
  }

  .artwork-title-bottom {
    font-size: 14px;
    padding: 8px 6px 6px;
  }
}

// 书法作品区域
.calligraphy-section {
  padding: 60px 0;
  background: #FFFFFF;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
}

.calligraphy-container {
  margin-top: 20px;
}

.calligraphy-row-one {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr; // 三等份布局
  gap: 24px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.calligraphy-row-two {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

// 第一份：3张图的布局
.section-three-images {
  display: grid;
  grid-template-rows: 179px 179px; // 上下各179px，总计358px + 12px间距 = 370px
  gap: 12px;
  height: 370px; // 与大卡片保持一致的高度
}

.top-two-images {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.bottom-one-image {
  display: flex;
  justify-content: stretch;
  
  .calligraphy-card {
    width: 100%; // 占满整个容器宽度，等于上面两张图 + 间距的总宽度
    flex: 1; // 确保填满容器
  }
}

// 第二份：2张图的布局
.section-two-images {
  display: grid;
  grid-template-rows: 179px 179px; // 上下各179px，总计358px + 12px间距 = 370px
  gap: 12px;
  height: 370px; // 与大卡片保持一致的高度
}

// 第三份：1张图的布局
.section-one-image {
  display: flex;
  align-items: stretch;
  height: 370px; // 大卡片高度370px
  
  @media (max-width: 768px) {
    height: 300px; // 平板端缩短高度
  }
  
  @media (max-width: 480px) {
    height: 250px; // 手机端进一步缩短高度
  }
}

.calligraphy-card {
  cursor: pointer;

  .calligraphy-image {
    width: 100%;
    overflow: hidden;
    border-radius: 8px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  .calligraphy-info {
    padding: 16px 0;
    background: transparent;

    .calligraphy-title {
      font-size: 14px;
      font-weight: normal;
      margin-bottom: 4px;
      color: #555;
    }

    .calligraphy-author,
    .calligraphy-dynasty,
    .calligraphy-theme {
      font-size: 14px;
      color: var(--text-secondary);
      margin-bottom: 2px;
    }

    .calligraphy-theme {
      font-size: 12px;
      color: var(--primary-color);
      background: rgba(139, 69, 19, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
      display: inline-block;
      margin-top: 4px;
    }
  }

  &:hover {
    .calligraphy-image img {
      transform: scale(1.05);
    }
  }
}

// 小书法卡片（第一份的上面2张）- 179px高度
.small-cal-card {
  height: 179px; // 总高度179px
  
  .calligraphy-image {
    height: 139px; // 图片区域高度，预留40px给文字描述

    @media (max-width: 768px) {
      height: 119px; // 移动端预留60px给文字描述
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover; // 确保图片铺满
    }
  }

  .calligraphy-info {
    padding: 8px 0;
    height: 40px; // 固定文字描述区域高度
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 768px) {
      height: 60px;
    }

    .calligraphy-title {
      font-size: 14px;
      font-weight: normal;
      text-align: left;
      line-height: 1.2;
    }
  }
}

// 中等书法卡片（第一份的下面1张、第二份的2张）- 179px高度
.medium-cal-card {
  height: 179px; // 总高度179px
  
  .calligraphy-image {
    height: 139px; // 图片区域高度，预留40px给文字描述

    @media (max-width: 768px) {
      height: 119px; // 移动端预留60px给文字描述
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover; // 确保图片铺满
    }
  }

  .calligraphy-info {
    padding: 10px 0;
    height: 40px; // 固定文字描述区域高度
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 768px) {
      height: 60px;
    }

    .calligraphy-title {
      font-size: 14px;
      font-weight: normal;
      text-align: left;
      line-height: 1.2;
    }
  }
}

// 大书法卡片（第三份的1张）- 390*370比例
.large-cal-card {
  height: 370px; // 总高度370px
  
  @media (max-width: 768px) {
    height: 300px; // 平板端缩短高度
  }
  
  @media (max-width: 480px) {
    height: 250px; // 手机端进一步缩短高度
  }
  
  .calligraphy-image {
    height: 320px; // 图片区域高度，预留50px给文字描述

    @media (max-width: 768px) {
      height: 250px; // 平板端调整图片高度，预留50px给文字
    }
    
    @media (max-width: 480px) {
      height: 200px; // 手机端进一步缩短图片高度，预留50px给文字
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover; // 确保图片铺满
    }
  }

  .calligraphy-info {
    padding: 12px 0;
    height: 50px; // 固定文字描述区域高度
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 768px) {
      height: 50px; // 平板端保持标准高度
    }
    
    @media (max-width: 480px) {
      height: 50px; // 手机端保持标准高度
    }

    .calligraphy-title {
      font-size: 14px;
      font-weight: normal;
      text-align: left;
      line-height: 1.2;
    }
  }
}

// 等大书法卡片（第二行的4张）
.equal-cal-card {
  .calligraphy-image {
    height: 180px; // 缩短高度

    @media (max-width: 768px) {
      height: 160px;
    }
  }

  .calligraphy-info {
    padding: 12px 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .calligraphy-title {
      font-size: 14px;
      font-weight: normal;
      text-align: left;
      line-height: 1.2;
    }
  }
}

</style>
